import React from "react";
import { Link } from "react-router-dom";
import { createPageStyles } from "./MarketingPageStyles";
import { detectPageLocale } from "./pageLocale";

const styles = createPageStyles("dark", "stirred");

type Locale = "ko" | "en" | "ja";
type Row = [string, string, string, string];

const LOCALE_STORAGE_KEY = "stirred-privacy-locale";
const locales: Locale[] = ["ko", "en", "ja"];
const localeLabels: Record<Locale, string> = { ko: "KO", en: "EN", ja: "JA" };

const tableRows: Record<Locale, Row[]> = {
  ko: [
    ["로컬 이용 데이터", "진행 기록·점수·마스터 뱃지, 즐겨찾기, 내 바 재료·구매 목록, 나만의 레시피, 앱 설정값", "학습 진행 관리, 컬렉션·내 바 표시", "사용자 기기 내 (앱 로컬 저장소)"],
    ["광고 (Google AdMob)", "광고 식별자, 익명 기기 정보, 광고 성과 데이터", "앱 내 광고 표시", "Google 서버 (Google LLC)"],
    ["Firebase Analytics / Crashlytics", "앱 버전, 충돌 로그, 익명 기기·사용 정보", "앱 안정성 개선, 오류 분석, 기능 개선", "Firebase 서버 (Google LLC)"],
    ["결제 이벤트 (Firebase Analytics)", "구매·복원 등 결제 관련 이벤트, 익명 식별자 (결제수단·카드정보 미포함)", "결제 오류 분석, 기능 개선", "Firebase 서버 (Google LLC)"],
    ["푸시 알림 (Firebase Cloud Messaging)", "푸시 토큰, 기기 정보", "알림 발송", "Firebase 서버 (Google LLC)"],
    ["인앱 구매 (광고 제거)", "앱은 결제정보를 수집하지 않음 (구매 영수증은 각 스토어가 처리)", "구매 상태 확인 및 복원", "Apple App Store / Google Play"],
  ],
  en: [
    ["Local usage data", "Progress, scores, master badges, favorites, bar ingredients and shopping list, custom recipes, app settings", "Managing learning progress, collection and bar display", "On device (local app storage)"],
    ["Ads (Google AdMob)", "Advertising identifier, anonymous device information, ad performance data", "Displaying in-app ads", "Google servers (Google LLC)"],
    ["Firebase Analytics / Crashlytics", "App version, crash logs, anonymous device/usage information", "Stability improvement, error analysis, feature improvement", "Firebase servers (Google LLC)"],
    ["Purchase events (Firebase Analytics)", "Purchase/restore events with anonymous identifiers (no payment method or card data)", "Analyzing billing errors, feature improvement", "Firebase servers (Google LLC)"],
    ["Push notifications (Firebase Cloud Messaging)", "Push token, device information", "Delivering notifications", "Firebase servers (Google LLC)"],
    ["In-app purchase (ad removal)", "The app does not collect payment data (receipts are handled by each store)", "Verifying and restoring purchase status", "Apple App Store / Google Play"],
  ],
  ja: [
    ["ローカル利用データ", "進捗・スコア・マスターバッジ、お気に入り、マイバーの材料・買い物リスト、オリジナルレシピ、アプリ設定", "学習進捗の管理、コレクション・マイバー表示", "ユーザー端末内（ローカル保存）"],
    ["広告 (Google AdMob)", "広告識別子、匿名の端末情報、広告パフォーマンスデータ", "アプリ内広告の表示", "Google サーバー（Google LLC）"],
    ["Firebase Analytics / Crashlytics", "アプリバージョン、クラッシュログ、匿名の端末・利用情報", "安定性改善、エラー分析、機能改善", "Firebase サーバー（Google LLC）"],
    ["決済イベント (Firebase Analytics)", "購入・復元などの決済関連イベント、匿名識別子（決済手段・カード情報は含まない）", "決済エラーの分析、機能改善", "Firebase サーバー（Google LLC）"],
    ["プッシュ通知 (Firebase Cloud Messaging)", "プッシュトークン、端末情報", "通知の配信", "Firebase サーバー（Google LLC）"],
    ["アプリ内購入（広告削除）", "アプリは決済情報を収集しません（レシートは各ストアが処理）", "購入状態の確認と復元", "Apple App Store / Google Play"],
  ],
};

const content: Record<
  Locale,
  {
    crumb: string;
    title: string;
    accentTitle: string;
    subtitle: string;
    tableHead: Row;
    sections: Array<{ title: string; body?: string; list?: string[] }>;
    websiteStats: { title: string; body: string; list: string[] };
  }
> = {
  ko: {
    crumb: "Stirred Privacy",
    title: "Stirred,",
    accentTitle: "개인정보 처리방침",
    subtitle:
      "최종 업데이트일: 2026년 9월 1일. 본 정책의 운영 주체는 Daydream Factory입니다. Stirred는 회원가입 없이 사용할 수 있으며, 이용 기록은 모두 사용자 기기 안에 저장됩니다. 별도의 서버 계정에 개인정보를 수집하지 않습니다.",
    tableHead: ["구분", "수집 항목", "사용 목적", "저장 위치"],
    sections: [
      { title: "1. 수집하는 정보" },
      {
        title: "2. 데이터 사용 목적",
        list: [
          "학습 진행·점수·컬렉션 및 내 바 관리",
          "앱 내 광고 표시 (Google AdMob)",
          "광고 제거 구매 상태 확인 및 복원",
          "앱 안정성 개선 및 오류 분석",
          "알림(푸시) 발송",
        ],
      },
      {
        title: "3. 제3자 처리 위탁",
        body: "Stirred는 아래 서비스를 통해 일부 데이터를 처리합니다. 각 서비스의 개인정보 처리방침이 함께 적용됩니다.",
        list: [
          "Google AdMob (Google LLC)",
          "Firebase (Google LLC)",
          "Apple App Store / Google Play",
        ],
      },
      {
        title: "4. 광고 식별자",
        body:
          "광고 표시를 위해 기기의 광고 식별자(IDFA/GAID)가 사용될 수 있습니다. iOS의 앱 추적 투명성(ATT) 설정 또는 Android의 광고 설정에서 맞춤 광고를 제한할 수 있습니다.",
      },
      {
        title: "5. 데이터 보관 및 삭제",
        list: [
          "이용 기록은 기기에만 저장되며, 앱을 삭제하면 함께 삭제됩니다.",
          "AdMob·Firebase 관련 데이터는 Google의 보관 정책에 따릅니다.",
        ],
      },
      {
        title: "6. 이용자 권리 및 문의",
        body:
          "이용자는 언제든 앱 삭제로 데이터를 삭제할 수 있습니다. 개인정보 관련 문의: support@daydreamfactory.net",
      },
    ],
    websiteStats: {
      title: "7. 웹사이트 정보",
      body: "본 페이지(daydreamfactory.net)는 방문 통계 측정을 위해 익명화된 분석 도구를 사용할 수 있습니다.",
      list: ["수집 항목: 페이지 조회, 익명 기기/브라우저 정보", "목적: 사이트 개선"],
    },
  },
  en: {
    crumb: "Stirred Privacy",
    title: "Stirred,",
    accentTitle: "Privacy Policy",
    subtitle:
      "Last updated: September 1, 2026. This policy is operated by Daydream Factory. Stirred can be used without an account, and all usage records are stored on your device. We do not collect personal data into a separate server account.",
    tableHead: ["Category", "Collected items", "Purpose", "Storage location"],
    sections: [
      { title: "1. Information We Collect" },
      {
        title: "2. How We Use Data",
        list: [
          "Managing learning progress, scores, collection, and your bar",
          "Displaying in-app ads (Google AdMob)",
          "Verifying and restoring the ad-removal purchase",
          "Improving app stability and analyzing errors",
          "Delivering push notifications",
        ],
      },
      {
        title: "3. Third-Party Processing",
        body: "Stirred processes some data through the services below. Each service's own privacy policy also applies.",
        list: [
          "Google AdMob (Google LLC)",
          "Firebase (Google LLC)",
          "Apple App Store / Google Play",
        ],
      },
      {
        title: "4. Advertising Identifier",
        body:
          "The device advertising identifier (IDFA/GAID) may be used to display ads. You can limit personalized ads via App Tracking Transparency (ATT) settings on iOS or the ads settings on Android.",
      },
      {
        title: "5. Data Retention and Deletion",
        list: [
          "Usage records are stored only on your device and are deleted when you delete the app.",
          "AdMob and Firebase data follows Google's retention policy.",
        ],
      },
      {
        title: "6. Your Rights and Contact",
        body:
          "You can delete your data at any time by deleting the app. Privacy inquiries: support@daydreamfactory.net",
      },
    ],
    websiteStats: {
      title: "7. Website Information",
      body: "This page (daydreamfactory.net) may use anonymized analytics to measure visit statistics.",
      list: ["Collected: page views, anonymous device/browser information", "Purpose: site improvement"],
    },
  },
  ja: {
    crumb: "Stirred Privacy",
    title: "Stirred,",
    accentTitle: "プライバシーポリシー",
    subtitle:
      "最終更新日: 2026年9月1日。本ポリシーの運営主体は Daydream Factory です。Stirred は会員登録なしで利用でき、利用記録はすべて端末内に保存されます。別途のサーバーアカウントに個人情報を収集しません。",
    tableHead: ["区分", "収集項目", "利用目的", "保存場所"],
    sections: [
      { title: "1. 収集する情報" },
      {
        title: "2. データの利用目的",
        list: [
          "学習進捗・スコア・コレクション・マイバーの管理",
          "アプリ内広告の表示 (Google AdMob)",
          "広告削除の購入状態の確認と復元",
          "アプリの安定性改善とエラー分析",
          "プッシュ通知の配信",
        ],
      },
      {
        title: "3. 第三者への処理委託",
        body: "Stirred は以下のサービスを通じて一部データを処理します。各サービスのプライバシーポリシーも併せて適用されます。",
        list: [
          "Google AdMob (Google LLC)",
          "Firebase (Google LLC)",
          "Apple App Store / Google Play",
        ],
      },
      {
        title: "4. 広告識別子",
        body:
          "広告表示のため、端末の広告識別子（IDFA/GAID）が使用される場合があります。iOS のアプリトラッキング透明性（ATT）設定、または Android の広告設定でパーソナライズ広告を制限できます。",
      },
      {
        title: "5. データの保管と削除",
        list: [
          "利用記録は端末内にのみ保存され、アプリを削除すると一緒に削除されます。",
          "AdMob・Firebase 関連データは Google の保管ポリシーに従います。",
        ],
      },
      {
        title: "6. ユーザーの権利とお問い合わせ",
        body:
          "ユーザーはいつでもアプリの削除でデータを削除できます。プライバシーに関するお問い合わせ: support@daydreamfactory.net",
      },
    ],
    websiteStats: {
      title: "7. ウェブサイト情報",
      body: "本ページ（daydreamfactory.net）は訪問統計の測定のため匿名化された分析ツールを使用する場合があります。",
      list: ["収集項目: ページ閲覧、匿名の端末/ブラウザ情報", "目的: サイト改善"],
    },
  },
};

const StirredPrivacyPolicy: React.FC = () => {
  const [locale, setLocale] = React.useState<Locale>("en");

  React.useEffect(() => {
    setLocale(detectPageLocale(LOCALE_STORAGE_KEY, locales, "en"));
  }, []);

  React.useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
      document.documentElement.lang = locale;
    }
  }, [locale]);

  const copy = content[locale];

  return (
    <main style={styles.page}>
      <div style={styles.shell}>
        <div style={styles.topRow}>
          <Link to="/" style={styles.homeLink}>
            ← Daydream Factory
          </Link>
          <div style={styles.topRowMeta}>
            <div style={styles.localeSwitch} aria-label="Language switcher">
              {locales.map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => setLocale(item)}
                  style={{
                    ...styles.localeButton,
                    ...(locale === item ? styles.activeLocaleButton : {}),
                  }}
                >
                  {localeLabels[item]}
                </button>
              ))}
            </div>
            <span style={styles.crumb}>{copy.crumb}</span>
          </div>
        </div>

        <section style={styles.hero}>
          <h1 style={styles.title}>
            {copy.title}
            <br />
            <span style={styles.accentTitle}>{copy.accentTitle}</span>
          </h1>
          <p style={styles.subtitle}>{copy.subtitle}</p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>{copy.sections[0].title}</h2>
          <div style={styles.sectionCard}>
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    {copy.tableHead.map((item) => (
                      <th key={item} style={styles.thtd}>
                        {item}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tableRows[locale].map((row) => (
                    <tr key={row[0]}>
                      {row.map((cell) => (
                        <td key={cell} style={styles.thtd}>
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {copy.sections.slice(1).map((section) => (
          <section key={section.title} style={styles.section}>
            <h2 style={styles.h2}>{section.title}</h2>
            <div style={styles.sectionCard}>
              {section.body ? (
                <p style={styles.body}>
                  {section.body.includes("support@daydreamfactory.net") ? (
                    <>
                      {section.body.replace("support@daydreamfactory.net", "")}
                      <a href="mailto:support@daydreamfactory.net" style={styles.link}>
                        support@daydreamfactory.net
                      </a>
                    </>
                  ) : (
                    section.body
                  )}
                </p>
              ) : null}
              {section.list ? (
                <ul style={styles.list}>
                  {section.list.map((item) => (
                    <li key={item}>
                      {item === "Google AdMob (Google LLC)" ? (
                        <>
                          Google AdMob (Google LLC) -{" "}
                          <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer" style={styles.link}>
                            Google Privacy Policy
                          </a>
                        </>
                      ) : item === "Firebase (Google LLC)" ? (
                        <>
                          Firebase (Google LLC) -{" "}
                          <a href="https://firebase.google.com/support/privacy" target="_blank" rel="noreferrer" style={styles.link}>
                            Firebase Privacy &amp; Security
                          </a>
                        </>
                      ) : item === "Apple App Store / Google Play" ? (
                        <>
                          Apple App Store / Google Play -{" "}
                          <a href="https://www.apple.com/legal/privacy/" target="_blank" rel="noreferrer" style={styles.link}>
                            Apple Privacy Policy
                          </a>
                        </>
                      ) : (
                        item
                      )}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          </section>
        ))}

        <section style={styles.section}>
          <h2 style={styles.h2}>{copy.websiteStats.title}</h2>
          <div style={styles.sectionCard}>
            <p style={styles.body}>{copy.websiteStats.body}</p>
            <ul style={styles.list}>
              {copy.websiteStats.list.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        </section>
      </div>
    </main>
  );
};

export default StirredPrivacyPolicy;
